import {
    CREDIT_USE_STATE,
    type CreditUseState,
    PAYMENT_LINK_ABSENCE,
    type PaymentLinkAbsence,
    XERO_DOCUMENT_STATE,
    XERO_NOT_SENT,
    type XeroDocumentState,
    type XeroNotSent,
    type XeroPushOutcome,
    XERO_STATE,
    type XeroState,
} from '@/features/xero/api';

/*
  The badge labels and the three alerts are the prototype's, verbatim (PROTO_DATA.xeroStates,
  screens/dashboard.html frames 1.1a to 1.1d). Everything else here is ours: the prototype's badge
  opens a menu of the four states for reviewers, which is not product (G-038).
*/
export const COPY = {
    badge: {
        [XERO_STATE.synced]: 'Xero synced',
        [XERO_STATE.syncing]: 'Reconnecting…',
        [XERO_STATE.disconnected]: 'Xero disconnected',
        [XERO_STATE.notLinked]: 'Xero not linked',
    } satisfies Record<XeroState, string>,
    alert: {
        [XERO_STATE.syncing]: {
            title: 'Reconnecting to Xero',
            body: 'Hold on while the connection is re-established. Avoid creating orders until this finishes.',
        },
        [XERO_STATE.disconnected]: {
            title: 'Xero is not connected',
            body: 'Orders created now will not sync and invoices will not be raised. Reconnect before creating any new orders.',
        },
        [XERO_STATE.notLinked]: {
            title: 'Connect Xero to raise invoices automatically',
            body: 'Until Xero is linked, invoices have to be raised by hand and payment links sent manually.',
        },
    },
    action: {
        connecting: 'Connecting…',
        tryAgain: 'Try again',
        tryToConnect: 'Try to connect',
        reconnect: 'Reconnect',
        disconnect: 'Disconnect',
    },
    details: {
        label: 'Xero connection',
        synced: 'Connected to Xero',
        organisation: 'Organisation',
        checked: 'Last checked',
        connected: 'Connected',
        connectedBy: (name: string | null, on: string) => (name ? `${on} by ${name}` : on),
        /** The webhook stopped announcing changes; the check still reads payments (ALS-70). */
        updatesMissed: (when: string) =>
            `Xero’s updates stopped arriving ${when}. Payments are read every 15 minutes until the webhook is switched on again in Xero’s developer portal.`,
    },
    disconnect: {
        title: 'Disconnect Xero?',
        body: (organisation: string | null) =>
            `Invoices and credit notes stop going to ${organisation ?? 'Xero'} until an admin connects Xero again.`,
        confirm: 'Disconnect',
        cancel: 'Keep connected',
        done: 'Xero disconnected',
    },
    failed: 'Xero could not be updated',
    /**
     * One document's state on the order page (ALS-69). The prototype draws two of these words:
     * "Xero synced" on an order in Xero, "Not synced" for one that is not, and "Pushing to Xero…"
     * while a credit note goes. The reasons and Retry are ours: the prototype never fails a push.
     */
    document: {
        state: {
            [XERO_DOCUMENT_STATE.sent]: 'Xero synced',
            [XERO_DOCUMENT_STATE.waiting]: 'Pushing to Xero',
            [XERO_DOCUMENT_STATE.failed]: 'Not synced',
            [XERO_DOCUMENT_STATE.notSent]: 'Not synced',
        } satisfies Record<XeroDocumentState, string>,
        notSent: {
            [XERO_NOT_SENT.beforeXero]: () => 'Issued before Xero was connected',
            [XERO_NOT_SENT.byHand]: (reference: string | null) =>
                reference ? `Raised in Xero by hand as ${reference}` : 'Raised in Xero by hand',
        } satisfies Record<XeroNotSent, (reference: string | null) => string>,
        retryAt: (when: string) => `Tries again ${when}`,
        retry: 'Retry',
        retrying: 'Retrying…',
        /** What Retry came to, as the toast says it, keyed by the API's outcome. */
        pushed: {
            Sent: (number: string) => ({ tone: 'success', title: `${number} is in Xero` }) as const,
            AlreadySent: (number: string) =>
                ({
                    tone: 'info',
                    title: `${number} was already in Xero`,
                    description: 'Nothing was sent again.',
                }) as const,
            Waiting: (number: string) =>
                ({
                    tone: 'warning',
                    title: `${number} is not in Xero yet`,
                    description: 'It is tried again shortly.',
                }) as const,
            Failed: (number: string) =>
                ({ tone: 'error', title: `${number} is not in Xero` }) as const,
            /** The note is in Xero; only an application of its credit stopped (ALS-486). */
            NotApplied: (number: string) =>
                ({
                    tone: 'error',
                    title: `${number} is in Xero, but not all its credit was applied`,
                }) as const,
            /** The note is in Xero; its credit waits for an invoice the queue sends first (ALS-486). */
            NotAppliedYet: (number: string) =>
                ({
                    tone: 'warning',
                    title: `${number} is in Xero, but not all its credit is applied yet`,
                }) as const,
            Busy: (number: string) =>
                ({ tone: 'info', title: `${number} is being sent to Xero right now` }) as const,
        } satisfies Record<XeroPushOutcome, (number: string) => { tone: string; title: string }>,
        /** Under an invoice in Xero: when its payments were last read back, kept while Xero cannot be reached (ALS-70). */
        readOn: (when: string) => `Payments last read from Xero ${when}`,
        /** An invoice that differs in Xero, said beside what differs; nothing here can retry it. */
        differs: 'Differs in Xero',
        /** The same, once all of it was dismissed on the daily check (ALS-388), with who, when and why. */
        dismissed: (by: string, when: string, reason: string) =>
            `Dismissed by ${by}, ${when}: ${reason}`,
        /** Under the order's Xero reference: credit notes that did not reach Xero, which the Instalments & invoicing tab lists. */
        creditNotesNotSynced: (n: number) =>
            `${n} ${n === 1 ? 'credit note' : 'credit notes'} not synced`,
        /** A payment recorded here and sent to Xero (ALS-456), as Retry and its toast name it: by its amount, not its reference. */
        payment: (amount: string) => `${amount} payment`,
        /** Under the order's Xero reference: payments recorded here that did not reach Xero (ALS-456), which Payments recorded lists. */
        paymentsNotInXero: (n: number) => `${n} ${n === 1 ? 'payment' : 'payments'} not in Xero`,
    },
    /**
     * Wallet credit applied to an order, use by use, and where each stands in Xero (ALS-384). The
     * prototype draws none of it; "Allocate in Xero by hand" is the ticket's own words.
     */
    credit: {
        title: 'Wallet credit applied',
        columns: { on: 'Applied', amount: 'Amount', from: 'From', xero: 'Xero' },
        noNote: 'Wallet credit',
        offInvoice: (amount: string, invoiceNo: string) => `${amount} off ${invoiceNo}`,
        state: {
            [CREDIT_USE_STATE.waiting]: 'Allocating in Xero',
            [CREDIT_USE_STATE.allocated]: 'Allocated in Xero',
            [CREDIT_USE_STATE.byHand]: 'Allocate in Xero by hand',
            [CREDIT_USE_STATE.outOfSync]: 'Out of sync with Xero',
            [CREDIT_USE_STATE.failed]: 'Not allocated',
            [CREDIT_USE_STATE.done]: 'Allocated by hand',
        } satisfies Record<CreditUseState, string>,
        doneBy: (who: string | null, when: string) =>
            who ? `Marked done by ${who} ${when}` : `Marked done ${when}`,
        retry: (from: string, invoiceNo: string) => `Retry ${from} on ${invoiceNo}`,
        markDone: 'Mark as done',
        confirm: {
            title: 'Mark as allocated in Xero?',
            body: (amount: string, invoiceNo: string) =>
                `Only once the accountant has allocated ${amount} to ${invoiceNo} in Xero by hand. The order stops asking, and nothing is sent to Xero.`,
            confirm: 'Mark as done',
            cancel: 'Not yet',
        },
        marked: 'Marked as allocated in Xero',
        /** Under the order's Xero reference: credit somebody still has to see to, which the Payments tab lists. */
        needAttention: (n: number) =>
            `${n} credit ${n === 1 ? 'allocation needs' : 'allocations need'} attention`,
        /** A top-up's credit note on its passbook row. */
        creditNote: (number: string) => `Credit note ${number}`,
        outOfSync: {
            title: 'Out of sync with Xero',
            body: 'Xero holds less credit for this client than the wallet expects, so nothing more is allocated from it until the accountant puts it right. Retry or mark it done on the order.',
        },
    },
    /**
     * Xero's payment link on a card order's Payments tab (ALS-385). Ours: the prototype has no
     * screen for it, and the ticket places it beside what is outstanding.
     */
    paymentLink: {
        label: 'Payment link',
        open: 'Open in Xero',
        newTab: '(opens in a new tab)',
        copy: 'Copy link',
        copyLabel: 'Copy payment link',
        copied: 'Payment link copied',
        copyFailed: 'The link could not be copied',
        copyFailedHow: 'Open it in Xero and copy it from the address bar instead.',
        charges: (amount: string) => `Charges ${amount}`,
        /**
         * The link held while Xero's amount due is not the order's (26 Oct 2026): it would charge
         * Xero's. More in Xero is a payment recorded here and not matched there yet; less is one
         * taken in Xero and not read back here yet.
         */
        disagrees: {
            more: (due: string, gap: string) =>
                `Xero still shows ${due} due. ${gap} recorded here isn't matched in Xero yet, so the link would overcharge.`,
            less: (due: string, gap: string) =>
                `Xero shows ${due} due, ${gap} less than here. A payment taken in Xero isn't recorded here yet, so the link would undercharge.`,
            then: 'The link comes back once the two agree.',
        },
        includesTransfer:
            "It also charges the bank-transfer instalments: Xero's link takes everything still due.",
        unreadable: 'The payment link could not be read.',
        tryAgain: 'Try again',
        /**
         * Why there is none. Not offered says nothing: a bank-transfer order never shows a link. A
         * disagreement names the figures, so it has its own copy above.
         */
        absent: {
            [PAYMENT_LINK_ABSENCE.notOffered]: () => '',
            [PAYMENT_LINK_ABSENCE.nothingToPay]: () =>
                'Paid in full, so there is nothing to pay by link.',
            [PAYMENT_LINK_ABSENCE.transferDue]: (instalment: number | null) =>
                `${instalment === null ? 'The instalment due now is' : `Instalment ${instalment} is due`} by bank transfer, so there is no payment link until it is paid.`,
            [PAYMENT_LINK_ABSENCE.noInvoice]: () => 'No invoice yet, so there is no payment link.',
            [PAYMENT_LINK_ABSENCE.sending]: () =>
                'The invoice is on its way to Xero. Its payment link shows once it is there.',
            [PAYMENT_LINK_ABSENCE.notInXero]: () =>
                "The invoice is not in Xero, so there is no payment link. The order's Xero reference says why.",
            [PAYMENT_LINK_ABSENCE.issuedBeforeXero]: () =>
                'Issued before Xero was connected, so the admin never sent it to Xero. If it was raised there by hand, get its payment link in Xero.',
            [PAYMENT_LINK_ABSENCE.outsideAdmin]: () =>
                'This invoice was raised in Xero outside the admin. Get its payment link in Xero.',
            [PAYMENT_LINK_ABSENCE.notConnected]: () =>
                'Xero is not connected, so the payment link cannot be fetched.',
            [PAYMENT_LINK_ABSENCE.unreachable]: () =>
                'Xero could not be reached for the payment link.',
            [PAYMENT_LINK_ABSENCE.voided]: () =>
                'The invoice was voided in Xero, so it has no payment link.',
            [PAYMENT_LINK_ABSENCE.unavailable]: () => 'Xero has no payment link for this invoice.',
        } satisfies Record<
            Exclude<PaymentLinkAbsence, typeof PAYMENT_LINK_ABSENCE.disagrees>,
            (instalment: number | null) => string
        >,
    },
    /** What the page announces when Connect comes back from Xero, keyed by the API's #xero= outcome. */
    outcome: {
        connected: { tone: 'success', title: 'Xero connected' },
        cancelled: {
            tone: 'info',
            title: 'Xero was not connected',
            description: 'Access was not allowed in Xero.',
        },
        expired: {
            tone: 'warning',
            title: 'Xero was not connected',
            description:
                'The approval took too long or started in another browser. Try to connect again.',
        },
        several: {
            tone: 'warning',
            title: 'Xero was not connected',
            description: 'Choose one organisation when Xero asks, then try to connect again.',
        },
        unreachable: {
            tone: 'error',
            title: 'Xero could not be reached',
            description: 'Nothing was connected. Try again in a moment.',
        },
        refused: {
            tone: 'error',
            title: 'Xero refused the connection',
            description: 'Nothing was connected. Try again, or check the app in Xero.',
        },
    },
    /** The account select every product and line form shares (ALS-386). */
    accountSelect: {
        daily: 'Used daily',
        other: 'Other sales accounts',
        notOffered: 'no longer offered',
    },
} as const;
